<template>
  <div v-if="!anyFieldsPending">
    <div class="font-bold"> SSO Enforcement </div>
    <div class="text-neutral-text">
      Control whether SSO is optional or required for all users.
    </div>
    <BittsRadioGroupCards
      :initial-value="ssoOnlyToggle"
      :options="SSO_OPTIONS"
      :disabled="!ssoEnabled || isCurrentUserSso"
      data-testid="require-sso-group"
      class="mb-16 mt-16"
      @change="ssoOnlyToggle = !ssoOnlyToggle"
    >
      <template #label="{ option }">
        <div class="flex items-center gap-8">
          <span>{{ (option as SsoOption).label }}</span>
          <BittsTag
            v-if="(option as SsoOption).value === true"
            color="info"
            size="x-small"
            text="Recommended for SCIM"
          />
        </div>
      </template>
    </BittsRadioGroupCards>
    <BittsAlert
      v-if="isCurrentUserSso && !ssoOnlySaved"
      class="mb-16"
      message="SSO enforcement can only be configured by a non-SSO admin. Please ask a non-SSO admin to enforce SSO."
      color="info"
    />
    <BittsMultiselect
      v-if="ssoOnlyToggle"
      v-model="ssoExceptions"
      :form-label="{
        title: 'SSO Login Exceptions',
        helpText:
          'List of users who will keep their current login credentials when SSO is enforced',
      }"
      :options="usersEligibleForSSOException"
      :disabled="ssoOnlySaved || anyFieldsPending"
      :show-checkboxes="false"
      :show-selected-options="false"
      class="mt-16"
      data-testid="sso-exceptions-selector"
      mode="multiple"
      @update:model-value="ssoExceptionHandler"
    />
    <BittsAlert
      v-if="ssoOnlyToggle && !ssoOnlySaved"
      class="mt-12 mb-16 p-4"
      message="You cannot define these users again later. Make sure you are ready to make this change."
      color="warning"
    >
      <FontAwesomeIcon :icon="['fak', 'warning']" class="mx-2" />
    </BittsAlert>

    <div class="font-bold"> Default Roles for SSO Users </div>
    <div class="text-neutral-text">
      When SCIM is required, roles are managed by your identity provider. The
      role settings below will not apply.
    </div>
    <SelectSeatType
      v-if="pickedSSOSeat"
      class="w-full mt-16"
      data-testid="login-options-card-default-seat-type__input"
      :disable-seat-checks="true"
      :allow-integration-seat="false"
      :picked-seat="pickedSSOSeat"
      @seat-selected="handleChangeSeat"
    />
    <div class="flex mt-16 gap-12">
      <SelectCoreRole
        v-if="pickedSSOSeat"
        v-model="pickedSSOCoreRole"
        class="w-1/2"
        data-testid="login-options-card-default-full-access-role__input"
        :set-default-seat-on-mount="false"
        :picked-seat="pickedSSOSeat"
      />
      <SelectSalesRole
        v-if="pickedSSOSeat"
        v-model="pickedSSOSalesRole"
        class="w-1/2"
        data-testid="login-options-card-default-sales-role__input"
        :picked-seat="pickedSSOSeat"
      />
    </div>
  </div>
  <SSOConfigModal
    v-if="showSsoConfigModal"
    ref="ssoConfigModalRef"
    :sign-on-url="signOnUrl"
    :certificate="certificate"
    :saml-metadata-url="samlMetadataUrl"
    :saml-idp-issuer="samlIdpIssuer"
    :connection-name="connectionName"
    :acs-url="acsUrl ?? ''"
    :entity-id="entityId ?? ''"
    :org-login-url="orgLoginUrl ?? ''"
    @saved="handleSsoConfigModalSaved"
    @closed="showSsoConfigModal = false"
  />
</template>

<script setup lang="ts">
import {
  BittsAlert,
  BittsMultiselect,
  BittsRadioGroupCards,
  BittsTag,
} from '@crossbeam/bitts';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';

import SelectCoreRole from '@/components/team/SelectCoreRole.vue';
import SelectSalesRole from '@/components/team/SelectSalesRole.vue';
import SelectSeatType from '@/components/team/SelectSeatType.vue';

import SSOConfigModal from '@/pages/organization/LoginOptions/components/SSOConfigModal.vue';

import useAuth from '@/composables/useAuth';
import appConfig from '@/config';
import { FEATURE_FLAG } from '@/constants/feature_flags';
import { SSO_ONLY, SSO_OPTIONS } from '@/constants/me';
import { CORE, NO_ACCESS } from '@/constants/team';
import { SALES, V4_SEAT_OPTIONS } from '@/constants/team_v4';
import { useFeatureFlagStore, useRolesStore, useTeamStore } from '@/stores';
import { Role } from '@/types/roles';
import { SsoConfigSetup } from '@/types/sso_config';

type SsoOption = {
  label: string;
  value: boolean;
};

type SeatOption = {
  value: string;
  label: string;
  description: string;
};

const props = defineProps<{
  ssoEnabled: boolean;
  ssoConfigData: SsoConfigSetup | null;
}>();

const emit = defineEmits<{
  'update:state': [state: FormState];
}>();

type FormState = {
  anyFieldsPending: boolean;
  ssoOnlyToggle: boolean;
  metadataResaveRequested: boolean;
};

type FormData = {
  signOnUrl: string;
  certificate: string;
  samlMetadataUrl: string;
  pickedSSOCoreRole: string | null;
  pickedSSOSalesRole: string | null;
  ssoOnlyToggle: boolean;
  ssoExceptions: number[];
  ssoRoles: Role[];
};

const signOnUrl = ref('');
const certificate = ref('');
const samlMetadataUrl = ref('');
const samlIdpIssuer = ref('');
const connectionName = ref('');
const acsUrl = ref<string | null>(null);
const entityId = ref<string | null>(null);
const orgLoginUrl = ref<string | null>(null);

/* V4 Seat Options */
const pickedSSOSeat = ref<SeatOption | null>(null);
const pickedSSOCoreRole = ref<string | null>(null);
const pickedSSOSalesRole = ref<string | null>(null);

const defaultRole = ref<string | null>(null);
const defaultSalesRole = ref<string | null>(null);
const defaultSeatType = ref<string | null>(null);
const showSsoConfigModal = ref(false);
/* Saving the modal with an unchanged metadata URL is still a request to
re-fetch it, so the server can re-derive anything gated by a flag flipped
since the last save (Cross App Access) */
const metadataResaveRequested = ref(false);
const ssoConfigModalRef = ref<{
  getCurrentValues: () => {
    signOnUrl: string;
    certificate: string;
    samlMetadataUrl: string;
  };
} | null>(null);
const ssoOnlyToggle = ref(false);
const ssoOnlySaved = ref(false);
const { currentAuth, currentOrg, currentUser } = useAuth();

const teamStore = useTeamStore();
const rolesStore = useRolesStore();
const featureFlagStore = useFeatureFlagStore();

const { authorizations } = storeToRefs(teamStore);
const { allRoles, ready: rolesReady } = storeToRefs(rolesStore);

const isCurrentUserSso = computed(() => !!currentAuth.value?.sso);
const ssoExceptions = ref<number[]>([]);

const ssoRoles = computed(() => allRoles.value ?? []);

const hasIntegrationUserFlag = computed(() =>
  featureFlagStore.hasFeatureFlag(FEATURE_FLAG.TEMP_INTEGRATION_USER),
);

const hasNewSsoModalFlag = computed(() =>
  featureFlagStore.hasFeatureFlag(FEATURE_FLAG.NEW_SSO_MODAL),
);

const usersEligibleForSSOException = computed(() =>
  authorizations.value.reduce<{ label: string; value: number }[]>(
    (agg, auth) => {
      if (auth.sso || !auth.role_id) return agg;
      const role = rolesStore.getRoleById(auth.role_id);
      /* Non-SSO admins are always eligible; non-SSO integration users are
      eligible when the integration user feature flag is enabled */
      const isEligible =
        role?.name === 'Admin' ||
        (hasIntegrationUserFlag.value &&
          role?.role_type === 'integration_user');
      if (isEligible) {
        const hasFullName = auth.user.first_name && auth.user.last_name;
        const label = hasFullName
          ? `${auth.user.first_name} ${auth.user.last_name} (${auth.user.email})`
          : auth.user.email;
        agg.push({ label, value: auth.user.id });
      }
      return agg;
    },
    [],
  ),
);

const defaultCoreRoleName = computed(
  () =>
    ssoRoles.value.find((item) => item.role_type === 'limited_user')?.name ??
    null,
);

/* An org configured manually before the flag stays configured */
const anyFieldsPending = computed(() => {
  const manualPending = !signOnUrl.value || !certificate.value;
  return hasNewSsoModalFlag.value
    ? !samlMetadataUrl.value && manualPending
    : manualPending;
});

const certificatePreview = computed(() => {
  if (!certificate.value) return '';
  const lines = certificate.value.split('\n');
  const beginIndex = lines.findIndex(
    (line) => line.trim() === '-----BEGIN CERTIFICATE-----',
  );
  if (beginIndex === -1 || beginIndex + 1 >= lines.length) return '';
  const certificateLine = lines[beginIndex + 1]?.trim() || '';
  const firstSeven = certificateLine.substring(0, 7);
  return firstSeven ? `${firstSeven}*******...` : '';
});

/* What the Login Options card shows next to the edit button. Under the new
modal the certificate only arrives from the server after the page-level save,
so the metadata URL is the thing the user can see and edit in the meantime */
const configSummary = computed(() => {
  if (hasNewSsoModalFlag.value && samlMetadataUrl.value) {
    return `Metadata ${samlMetadataUrl.value}`;
  }
  return certificatePreview.value
    ? `Certificate ${certificatePreview.value}`
    : '';
});

function findDefaultCoreRoleId(roleName: string | null): string | null {
  if (!roleName) return null;
  const defaultRoleItem = ssoRoles.value.find((item) => item.name === roleName);
  return defaultRoleItem?.id?.toString() ?? null;
}

function setDefaults() {
  pickedSSOSeat.value =
    V4_SEAT_OPTIONS.find((seat) => seat.value === defaultSeatType.value) ??
    null;
  pickedSSOSalesRole.value = defaultSalesRole.value;
  pickedSSOCoreRole.value = defaultRole.value;

  /* If there's no default seat or the previous seat was removed, use the Core as a default Seat Type */
  if (!defaultSeatType.value || !pickedSSOSeat.value) {
    pickedSSOSeat.value =
      V4_SEAT_OPTIONS.find((seat) => seat.value === CORE) ?? null;
  }

  /* For a core seat type, If there's no default role or the previous role was removed, use the view only role */
  if (
    (!defaultRole.value || !pickedSSOCoreRole.value) &&
    pickedSSOSeat.value?.value === CORE &&
    defaultCoreRoleName.value
  ) {
    pickedSSOCoreRole.value = findDefaultCoreRoleId(defaultCoreRoleName.value);
  }

  /* For a core seat type, null from the API means "No Access" — map it to
     NO_ACCESS so the dropdown shows "No Access" instead of the placeholder */
  if (pickedSSOSeat.value?.value === CORE && !pickedSSOSalesRole.value) {
    pickedSSOSalesRole.value = NO_ACCESS;
  }
}

function resetSsoSettings() {
  ssoOnlyToggle.value = false;
  samlMetadataUrl.value = '';
  samlIdpIssuer.value = '';
  connectionName.value = '';
  acsUrl.value = null;
  entityId.value = null;
  orgLoginUrl.value = null;
  defaultRole.value = null;
  defaultSalesRole.value = null;
  defaultSeatType.value = null;
}

/* This is a hack: You cannot unselect yourself if adding exception users,
and if you've required SSO, we do not let you change anything */
function ssoExceptionHandler() {
  if (ssoExceptions.value.find((value) => value === currentUser.value.id))
    return;
  if (!ssoOnlySaved.value) ssoExceptions.value.push(currentUser.value.id);
}

function handleOpenSsoConfigModal() {
  showSsoConfigModal.value = true;
}

function handleSsoConfigModalSaved({
  signOnUrl: newSignOnUrl,
  certificate: newCertificate,
  samlMetadataUrl: newSamlMetadataUrl,
}: {
  signOnUrl: string;
  certificate: string;
  samlMetadataUrl: string;
}) {
  signOnUrl.value = newSignOnUrl;
  certificate.value = newCertificate;
  samlMetadataUrl.value = newSamlMetadataUrl;
  metadataResaveRequested.value =
    hasNewSsoModalFlag.value && !!newSamlMetadataUrl;
  showSsoConfigModal.value = false;
}

function handleChangeSeat(newSeat: SeatOption) {
  pickedSSOSeat.value = newSeat;
  if (newSeat.value === SALES) pickedSSOCoreRole.value = null;

  if (newSeat.value === CORE && !pickedSSOCoreRole.value) {
    if (defaultRole.value) {
      pickedSSOCoreRole.value = defaultRole.value;
    } else if (defaultCoreRoleName.value) {
      pickedSSOCoreRole.value = findDefaultCoreRoleId(
        defaultCoreRoleName.value,
      );
    }
  }
}

function getFormData(): FormData {
  // If the SSO config modal is open, use its current values (which may be unsaved)
  // Otherwise, use the form's saved values
  const modalValues = ssoConfigModalRef.value?.getCurrentValues();
  const currentSignOnUrl = modalValues?.signOnUrl ?? signOnUrl.value;
  const currentCertificate = modalValues?.certificate ?? certificate.value;

  return {
    signOnUrl: currentSignOnUrl,
    certificate: currentCertificate,
    samlMetadataUrl: modalValues?.samlMetadataUrl ?? samlMetadataUrl.value,
    pickedSSOCoreRole: pickedSSOCoreRole.value,
    pickedSSOSalesRole:
      pickedSSOSalesRole.value === NO_ACCESS ? null : pickedSSOSalesRole.value,
    ssoOnlyToggle: ssoOnlyToggle.value,
    ssoExceptions: ssoExceptions.value,
    ssoRoles: ssoRoles.value,
  };
}

// Initialize from props
watch(
  () => props.ssoConfigData,
  (configData: SsoConfigSetup | null) => {
    metadataResaveRequested.value = false;
    if (configData) {
      signOnUrl.value = configData.login_url || '';
      certificate.value = configData.certificate || '';
      samlMetadataUrl.value = configData.saml_metadata_url ?? '';
      samlIdpIssuer.value = configData.saml_idp_issuer ?? '';
      // @ts-expect-error connection_name is not in the OAS yet
      connectionName.value = configData.connection_name ?? '';
      acsUrl.value = configData.acs_url ?? null;
      entityId.value = configData.entity_id ?? null;
      const configWithId = configData as SsoConfigSetup & { id?: string };
      orgLoginUrl.value = configWithId.id
        ? `https://${appConfig.host}/login?sso=${configWithId.id}`
        : null;
      defaultRole.value = configData.default_role ?? null;
      defaultSalesRole.value = configData.default_sales_edge_role ?? null;
      defaultSeatType.value = configData.default_seat_type ?? null;
      setDefaults();
    } else {
      resetSsoSettings();
    }
  },
  { immediate: true },
);

// Initialize SSO enforcement and exceptions
watch(
  [() => currentOrg.value.login_method, () => teamStore.ready],
  ([loginMethod, isTeamStoreReady]) => {
    const enforcement = loginMethod === SSO_ONLY;
    ssoOnlyToggle.value = enforcement;
    ssoOnlySaved.value = enforcement;

    if (enforcement) {
      // When SSO is enforced, only initialize with all eligible users if:
      // 1. ssoExceptions is empty (preserves saved exceptions after a save operation)
      // 2. teamStore is ready (ensures authorizations are loaded)
      if (ssoExceptions.value.length === 0 && isTeamStoreReady) {
        const exemptedUserIds = usersEligibleForSSOException.value.map(
          (item) => item.value,
        );
        ssoExceptions.value = exemptedUserIds;
      }
    } else {
      // When SSO is not enforced, initialize with just current user
      ssoExceptions.value = [currentUser.value.id];
    }
  },
  { immediate: true },
);

// Watch ssoEnabled prop and set ssoOnlyToggle to false when SSO is disabled
watch(
  () => props.ssoEnabled,
  (newValue) => {
    if (!newValue) {
      ssoOnlyToggle.value = false;
    }
  },
);

// Watch for roles to become available and re-apply fallback logic if needed
// This fixes the race condition where setDefaults() runs before roles are loaded
watch(
  [
    () => rolesReady.value,
    () => ssoRoles.value.length,
    () => props.ssoConfigData,
  ],
  ([isRolesReady, rolesLength, configData]) => {
    if (
      isRolesReady &&
      rolesLength > 0 &&
      configData &&
      pickedSSOSeat.value?.value === CORE &&
      (!defaultRole.value || !pickedSSOCoreRole.value) &&
      defaultCoreRoleName.value
    ) {
      if (!pickedSSOCoreRole.value) {
        pickedSSOCoreRole.value = findDefaultCoreRoleId(
          defaultCoreRoleName.value,
        );
      }
    }
  },
  { immediate: true },
);

// Emit state updates for parent computed properties
watch(
  [anyFieldsPending, () => ssoOnlyToggle.value, metadataResaveRequested],
  ([pending, toggle, resave]) => {
    emit('update:state', {
      anyFieldsPending: pending,
      ssoOnlyToggle: toggle,
      metadataResaveRequested: resave,
    });
  },
  { immediate: true },
);

// Expose only methods needed by parent
defineExpose({
  handleOpenSsoConfigModal,
  getFormData,
  certificatePreview,
  configSummary,
});
</script>
